import ContentType from '../model/contentType';
import { trackViewerEvent } from '../service/ViewerService';
import ApplicationEntity from './appEntity';
import { EventType, ANONYMOUS_PATTERN,
    UniversalPlayerMode, ManifestFormatType, DOWNLOAD_MODE } from './constants';

export const getParameterByName = (name, url) => {
    let inputUrl = url;
    let paraName = name;
    if (!inputUrl) {
        inputUrl = window.location.href;
    }

    paraName = paraName.replace(/[[\]]/g, '\\$&');
    const regex = new RegExp(`[?&]${paraName}(=([^&#]*)|&|#|$)`);
    const results = regex.exec(inputUrl);

    if (!results) return null;
    if (!results[2]) return '';
    return decodeURIComponent(results[2].replace(/\+/g), ' ');
};

export const apiDomainResolver = (baseUrl) => {
    const localService = getParameterByName('localService');
    if (localService) {
        return localService;
    }

    return baseUrl;
};

export const getLinkToken = () => {
    const isRunDemo = window.isRunDemo || false;
    if (getParameterByName('useMockData') || isRunDemo) {
        return 'mockLinkId';
    }

    const infos = window.location.pathname.split('/').slice(1);
    return infos[1];
};

export const getUrlParameters = () => window.btoa(window.location.search);

export const getContentTypeMapping = (translate, contents, singleUse = true) => {
    if (contents && contents.length > 1) {
        return translate('LIVE_SEND_VIEWER_CONTENT');
    }

    const contentType = contents[0].format.toUpperCase();

    switch (contentType) {
    case ContentType.PDF:
    case ContentType.DOC:
    case ContentType.DOCX:
        return singleUse ? translate('LIVE_SEND_VIEWER_A_DOCUMENT') : translate('LIVE_SEND_VIEWER_DOCUMENT');
    case ContentType.PPT:
    case ContentType.PPTX:
        return singleUse ? translate('LIVE_SEND_VIEWER_A_PRESENTATION') : translate('LIVE_SEND_VIEWER_PRESENTATION');
    case ContentType.VIDEO:
    case ContentType.MP4:
    case ContentType.AVI:
    case ContentType.WMV:
    case ContentType.MOV:
    case ContentType.VIMEO:
    case ContentType.YOUTUBE:
        return singleUse ? translate('LIVE_SEND_VIEWER_A_VIDEO') : translate('LIVE_SEND_VIEWER_VIDEO');
    case ContentType.PNG:
    case ContentType.JPG:
    case ContentType.JPEG:
        return singleUse ? translate('LIVE_SEND_VIEWER_AN_IMAGE') : translate('LIVE_SEND_VIEWER_IMAGE');
    default:
        return singleUse ? translate('LIVE_SEND_VIEWER_A_FILE') : translate('LIVE_SEND_VIEWER_FILE');
    }
};

export const UXContextType = {
    ENGAGEMENT_VIEWER: 'Engagement Viewer',
    HEADER: 'Header',
    FOOTER: 'Footer',
    VIEWER_PANEL: 'ViewerPanel',
    PASSWORD: 'Password',
    SPLASH_SCREEN: 'SplashScreen',
    SHARE_MODAL: 'ShareModal'
};

export const initApplicationInfo = (imageURL) => {
    ApplicationEntity.getInstance().imageUrl = imageURL;
    if (!window.LiveSend && window.jQuery) {
        window.LiveSend = window.jQuery.connection.LiveSendHub;
    }
};

export const getImageUrl = (imagePath) => {
    const relativePath = imagePath.substring(imagePath.lastIndexOf('seismic-engagement-viewer-ui'));

    if (ApplicationEntity.getInstance().imageUrl) {
        return `${ApplicationEntity.getInstance().imageUrl}${relativePath}`;
    }

    return imagePath;
};

export const toLocalDate = (utcDate) => {
    const date = new Date(utcDate);
    const offset = date.getTimezoneOffset() * 60 * 1000;
    const localDate = new Date(date.getTime() - offset);
    return localDate;
};

export const sendDownloadedEvent = (contentId, downloadFormat) => {
    const sessionId = ApplicationEntity.getInstance().sessionId;
    if (contentId && sessionId) {
        const linkToken = getLinkToken();
        const viewerEventRequestModel = {
            sessionId,
            eventType: EventType.DOWNLOADED,
            detail: contentId,
            extensions: {
                downloadFormat: downloadFormat || DOWNLOAD_MODE.OriginalFormat
            }
        };
        trackViewerEvent(linkToken, viewerEventRequestModel);
    }
};

export const isSellerPreviewing = () => {
    const sellerPreviewing = getParameterByName('sellerPreviewing');
    return sellerPreviewing === 'true';
};

export const downloadFile = ({ downloadUrl, contentId, downloadFormat }, isSellerPreview = false) => {
    // #region Fix S40 preview download issue
    if (isSellerPreview) {
        const a = document.createElement('a');
        a.style.display = 'none';
        a.target = '_blank';
        document.body.appendChild(a);
        a.href = downloadUrl;
        a.click();
        document.body.removeChild(a);
        return;
    }
    // #endregion

    const DOWNLOAD_IFRAME_ID = 'download-iframe';
    sendDownloadedEvent(contentId, downloadFormat);
    let downloadIframe = document.getElementById(DOWNLOAD_IFRAME_ID);
    if (downloadIframe) {
        // avoid planty of iframe tags in the dom
        downloadIframe.src = downloadUrl;
    } else {
    // use downloadUrl returned from PS to download file
        downloadIframe = document.createElement('iframe');
        downloadIframe.setAttribute('id', DOWNLOAD_IFRAME_ID);
        downloadIframe.src = downloadUrl;
        downloadIframe.style.display = 'none';
        document.body.appendChild(downloadIframe);
    }
};

const iOSAfterV13 = () => {
    // https://stackoverflow.com/questions/57765958/how-to-detect-ipad-and-ipad-os-version-in-ios-13-and-up
    if (/iP(hone|od|ad)/.test(navigator.userAgent)) {
        /* iPhone OS 13+ */
        const v = (navigator.appVersion).match(/OS (\d+)_(\d+)_?(\d+)?/);
        const version = [parseInt(v[1], 10), parseInt(v[2], 10), parseInt(v[3] || 0, 10)];
        return version[0] >= 13;
    } else if (window.navigator.platform === 'MacIntel') {
        /* iPad OS 13+ */
        return window.navigator.maxTouchPoints > 1;
    }

    return false;
};

export const isMobileDevice = {
    Android: () => (window.navigator.userAgent.match(/Android/i)),
    BlackBerry: () => (window.navigator.userAgent.match(/BlackBerry/i)),
    iPad: () => (window.navigator.userAgent.match(/(iPad)/) /* iOS pre 13 */ ||
        (window.navigator.platform === 'MacIntel' && window.navigator.maxTouchPoints > 1) /* iPad OS 13+ */),
    iOS: () => (window.navigator.userAgent.match(/iPhone|iPad|iPod|ios/i) || isMobileDevice.iPad()),
    MAC: () => (window.navigator.userAgent.match(/\(i[^;]+;( U;)? CPU.+Mac OS X/)),
    Windows: () => (window.navigator.userAgent.match(/IEMobile/i)),
    any: () => (isMobileDevice.Android() || isMobileDevice.BlackBerry() || isMobileDevice.iOS() || isMobileDevice.Windows())
};

export const canDownloadInDevice = () => !isMobileDevice.iOS() || (isMobileDevice.iOS() && iOSAfterV13());

export const browserMatch = {
    Edge: () => (navigator.userAgent.match(/Edge/i)),
    IE: () => (document.documentMode || +(navigator.userAgent.match(/MSIE (\d+)/) && RegExp.$1)),
    SAFARI: () => (/Safari/.test(navigator.userAgent) && !/Chrome/.test(navigator.userAgent))
};

export const getViewerTimeInfo = () => {
    const $ = window.jQuery;
    let dateStr = '';

    let currentLang = window.navigator.language;
    if (!currentLang) { currentLang = window.navigator.browserLanguage; }

    const timeZone = 0 - (new Date().getTimezoneOffset() / 60);

    if (currentLang.substr(0, 2).toLowerCase() === 'en') {
        dateStr = new Date().toString().substring((new Date().toString()).indexOf('GMT'));
    } else {
        if (isMobileDevice.any()) {
            dateStr = '';
        } else {
            dateStr = $.trim(new Date().toLocaleString('en-US', { timeZoneName: 'long', year: '2-digit' }).substring(3));
        }

        let timeZoneStr = timeZone < 0 ? 'GMT' : 'GMT+';
        if (parseInt(timeZone, 10) === timeZone) {
            timeZoneStr += `${(`${timeZone}`).replace(/\b\d\b/g, '0$&')}00`;
        } else {
            timeZoneStr += (`${parseInt(timeZone, 10)}`).replace(/\b\d\b/g, '0$&') + (Math.abs(timeZone - parseInt(timeZone, 10)) * 60);
        }

        dateStr = dateStr === '' ? timeZoneStr : `${timeZoneStr} (${dateStr})`;
    }

    return dateStr;
};

export const isUrl = format => format.toUpperCase() === ContentType.URL;

export const supportDownload = (format) => {
    const formatUpperCase = format.toUpperCase();
    return !isUrl(format) && formatUpperCase !== ContentType.VIMEO
        && formatUpperCase !== ContentType.YOUTUBE;
};

export const isMobilePhone = () => {
    const ua = window.navigator.userAgent;
    return isMobileDevice.any() && !(/(?:iPad|PlayBook)/.test(ua) || (isMobileDevice.Android() && !/(?:Mobile)/.test(ua)));
};

export const getFormatIcon = (format, url) => {
    const serviceAddress = ApplicationEntity.getInstance().serviceAddress;
    let iconHost = (serviceAddress && serviceAddress.ih) || 'https://api.seismic.com';
    iconHost = iconHost.endsWith('/') ? iconHost.substring(0, iconHost.length - 1) : iconHost;
    if (isUrl(format)) {
        return `${iconHost}/icon-host/v1/content-icons-v2/icon?format=${format}&url=${url}`;
    }

    return `${iconHost}/icon-host/v1/content-icons-v2/icon?format=${format}`;
};

export const getSenderInfoDetails = (sender, tenant) => {
    const senderInfo = { ...sender };
    if (ANONYMOUS_PATTERN.test(sender.name)) {
        senderInfo.isAnonymousSender = true;
        senderInfo.name = tenant;
        return senderInfo;
    }

    senderInfo.isAnonymousSender = false;

    if (sender.firstName || sender.lastName) {
        senderInfo.name = `${sender.firstName} ${sender.lastName}`;
    } else if (sender.email) {
        senderInfo.name = sender.email;
    } else {
        senderInfo.name = sender.name;
    }

    return senderInfo;
};

// TODO: remove this function when the "useDefaultMessage" is available
export const shouldUseDefaultMessage = (gdprSetting) => {
    const defaultDisclaimerMessage = 'By selecting "View [[content type]]" above and interacting with these materials, you are consenting to sharing activity and insights with the sender of this content.';
    const { disclaimerMessage, useDefaultMessage } = gdprSetting;

    if (useDefaultMessage !== undefined && useDefaultMessage !== null) {
        return useDefaultMessage;
    }

    const disclaimer = (disclaimerMessage || '').replace('<p>', '').replace('</p>', '').replace('&quot;', '"').replace('&quot;', '"');
    const isDefaultMessage = !disclaimer || disclaimer === defaultDisclaimerMessage;
    return isDefaultMessage;
};

export const getBrowserLang = () => {
    const browserLang = window.navigator.language;
    return browserLang || '';
};

export const isSpecificFormat = (manifest, format) => {
    if (manifest && manifest.Manifests &&
        manifest.Manifests.some(m => m.FormatType.toUpperCase() === format.toUpperCase())) {
        return true;
    }

    return false;
};

export const isPPTXOrGSLIDES = manifest => manifest.NativeFormat && manifest.NativeFormat.toUpperCase().match(/^(PPTX?|GSLIDES)$/i);
export const isGenerating = manifest => manifest && manifest.ViewerDetail && manifest.ViewerDetail.FirestormIsGenerating;

export function isFirestormGenerating(manifests) {
    const presentModeManifest = manifests && manifests.Manifests && manifests.Manifests.find(m => m.Mode === UniversalPlayerMode.PRESENT && isPPTXOrGSLIDES(m) && isGenerating(m));
    return !!presentModeManifest;
}

export const isFirestorm = manifest => (isSpecificFormat(manifest, ManifestFormatType.FIRESTORM) || isFirestormGenerating(manifest));
export const isAudio = manifest => isSpecificFormat(manifest, ManifestFormatType.AUDIO);
export const isVideo = manifest => isSpecificFormat(manifest, ManifestFormatType.VIDEO);

export const isAudioVideoFirestorm = manifest => isFirestorm(manifest) || isAudio(manifest) || isVideo(manifest);

export const getCurrentManifest = (manifest) => {
    if (manifest && manifest.Manifests) {
        let currentManifest = {};
        if (isFirestorm(manifest)) {
            currentManifest = manifest.Manifests.find(m => m.Mode && m.Mode === UniversalPlayerMode.PRESENT);
        }

        currentManifest = manifest.Manifests.find(m => m.Mode && m.Mode === UniversalPlayerMode.READ);
        return currentManifest || manifest.Manifests[0];
    }

    return {};
};

export const getDefaultContentByQueryString = (contents, defaultContent) => {
    const uid = getParameterByName('initialDoc');
    const initContent = contents.find(content => content.sourceContentVersionId === uid);
    if (uid && initContent) {
        return {
            selectedItem: {
                id: initContent.id,
                cover: '',
                format: initContent.format,
                name: initContent.name,
                versionId: initContent.sourceContentVersionId
            },
            initialDoc: true
        };
    }

    return {
        selectedItem: {
            id: defaultContent.id,
            cover: '',
            format: defaultContent.format,
            name: defaultContent.name,
            versionId: defaultContent.sourceContentVersionId
        },
        initialDoc: false
    };
};

export const isShowSignature = () => {
    const signature = getParameterByName('signature');
    if (signature === 'none') {
        return false;
    }

    return true;
};

export const isExpiredByBetweenDays = (createdDate, daysBetween = 5) => {
    const BETWEEN_TIMES = 1000 * 60 * 60 * 24 * daysBetween;
    const currentDate = new Date();
    const startDate = new Date(createdDate);
    const utcTime = new Date(currentDate.getUTCFullYear(), currentDate.getUTCMonth(), currentDate.getUTCDate(),
        currentDate.getUTCHours(), currentDate.getUTCMinutes(), currentDate.getUTCSeconds());
    const diffTimes = Math.abs(utcTime.getTime() - startDate.getTime());
    return diffTimes > BETWEEN_TIMES;
};

export const EMAIL_REGEX = /^(?:[a-zA-Z0-9][a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]*[a-zA-Z0-9]*\.)*(([a-zA-Z0-9][a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]*[a-zA-Z0-9])|([a-zA-Z0-9]+))@(?:[a-zA-Z0-9][a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]*[a-zA-Z0-9]*\.)+[a-zA-Z0-9][a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]{0,22}[a-zA-Z0-9]$/;

export const isNoTrackView = () => {
    const noTrack = getParameterByName('noTrack');
    const sellerPreviewing = getParameterByName('sellerPreviewing');
    return noTrack === 'true' || sellerPreviewing === 'true';
};

export const isAcceptView = () => {
    const value = getParameterByName('accept');
    return value === 'true';
};

export const hasDismissSplashScreen = (isPasswordRequired) => {
    if (isAcceptView() && !isPasswordRequired) {
        return true;
    }

    if (isNoTrackView() && !isPasswordRequired) {
        return true;
    }

    return false;
};

export const awaitWrap = promise => (
    promise
        .then(data => [null, data])
        .catch(err => [err, null])
);

export const getBESUrl = () => {
    const serviceAddress = ApplicationEntity.getInstance().serviceAddress;
    let besAddress = serviceAddress && serviceAddress.bes;
    besAddress = besAddress && besAddress.replace('https://', '');
    return besAddress;
};

export const supportPDFFormats = ['DOC', 'DOCX', 'PPT', 'PPTX', 'KEY', 'GDOC', 'GSLIDES'];
export const supportMultipleDownloads = format => format && supportPDFFormats.includes(format.toUpperCase());

export const getContentDownloadAssets = (downloadAssets, selectedContent) => {
    const selectedContentAsset = downloadAssets && downloadAssets.find(c => c.contentVersionId === selectedContent.versionId ||
        (!c.contentVersionId && c.contentId === selectedContent.versionId));
    const contentAssets = selectedContentAsset && selectedContentAsset.assets;
    // filter the duplicate download format
    const downloadUrls = [];
    if (contentAssets) {
        const PDFAsset = contentAssets.find(a => a && a.assetType === DOWNLOAD_MODE.PDFOnly);
        if (PDFAsset) {
            downloadUrls.push(PDFAsset);
        }

        const originalAsset = contentAssets.find(a => a && a.assetType === DOWNLOAD_MODE.OriginalFormat);
        if (originalAsset) {
            downloadUrls.push(originalAsset);
        }
    }

    return downloadUrls;
};

export const getDownloadUrl = (format, downloadFormats, downloadUrls) => {
    if (downloadUrls.length === 1 && downloadFormats === DOWNLOAD_MODE.PDFOrOriginal) {
        return downloadUrls[0];
    }

    if (format && format.toUpperCase() === 'PDF') {
        // handle special case for google doc
        const hasPdfUrl = downloadUrls && downloadUrls.some(d => d.assetType === DOWNLOAD_MODE.PDFOnly);
        const downloadFormat = hasPdfUrl ? DOWNLOAD_MODE.PDFOnly : DOWNLOAD_MODE.OriginalFormat;
        return downloadUrls && downloadUrls.find(d => d.assetType === downloadFormat);
    }

    const downloadFormat = supportMultipleDownloads(format) && downloadFormats !== DOWNLOAD_MODE.PDFOrOriginal ? downloadFormats : DOWNLOAD_MODE.OriginalFormat;
    const currentDownloadUrl = downloadUrls && downloadUrls.find(d => d.assetType === downloadFormat);
    return currentDownloadUrl;
};

export const isShowErrorPape = (isWatermarkingTrackingOptOut, isWatermarkingRequired, isStrictlyNecessaryCookies, isOnlyFunctionalCookies) => {
    if (isNoTrackView()) {
        return false;
    }

    if (isWatermarkingTrackingOptOut) {
        return true;
    }

    if (isWatermarkingRequired && (isStrictlyNecessaryCookies || isOnlyFunctionalCookies)) {
        return true;
    }

    return false;
};
